import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Container, Text, Group, Loader, createStyles } from '@mantine/core';
import { BusinessDashboardView } from '../../components/DashBoard/BusinessDashboard/BusinessDashboardView';
import { MarketingDashboardView } from '../../components/DashBoard/MarketingDashboard/MarketingDashboardView';
import { ScopeLine } from '../../components/DashBoard/ScopeLine';
import DashBoardApi from '../../api/DashBoardApi';

const useStyles = createStyles((theme) => ({
  shell: {
    borderRadius: 12,
    overflow: 'hidden',
    border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : '#E8E8E8'}`,
    background: theme.colorScheme === 'dark' ? theme.colors.dark[7] : '#FFFFFF',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  topbar: {
    borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : '#EBEBEB'}`,
    padding: '18px 28px',
  },
  dashName: {
    fontSize: 17,
    fontWeight: 600,
    color: theme.colorScheme === 'dark' ? theme.white : '#1A1A1A',
  },
  snapshotBadge: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    background: '#EEF2FF',
    color: '#4338CA',
    padding: '4px 10px',
    borderRadius: 4,
  },
  expiryBar: {
    background: '#FFFBEB',
    borderBottom: '1px solid #FDE68A',
    padding: '10px 28px',
    fontSize: 12,
    color: '#92400E',
    textAlign: 'center' as const,
  },
  errorShell: {
    padding: 60,
    textAlign: 'center' as const,
  },
}));

/**
 * Public read-only dashboard viewer — no authentication required.
 * Renders the frozen snapshot payload stored by `create_dashboard_snapshot_link`.
 *
 * Spec rules (Marketing Dashboard Section 7 — Export & Sharing):
 *   - No Visiopt login required
 *   - Data is frozen at the moment the link was generated
 *   - Link expires after 14 days
 *   - Recipient cannot edit settings, change date range, or interact with
 *     the projection slider (we pass loading=true to the marketing view to
 *     lock its interactive controls)
 *   - Cannot regenerate or extend the link
 */
export function DashboardSnapshot() {
  const { classes } = useStyles();
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) {
        setError('Missing snapshot token.');
        setLoading(false);
        return;
      }
      try {
        const response = await DashBoardApi.getDashboardSnapshotByToken({ snapshot_token: token });
        const data = await response.json();
        if (cancelled) return;
        if (data?.status === 1 && data?.data) {
          setSnapshot(data.data);
        } else {
          setError(data?.message || 'Snapshot not found.');
        }
      } catch {
        if (!cancelled) setError('Unable to load snapshot. Please try again later.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <Container size="xl" pt={40}>
        <Card className={classes.shell}>
          <div className={classes.errorShell}>
            <Loader size="sm" />
            <Text mt={12} size="sm" color="dimmed">Loading snapshot…</Text>
          </div>
        </Card>
      </Container>
    );
  }

  if (error || !snapshot) {
    return (
      <Container size="xl" pt={40}>
        <Card className={classes.shell}>
          <div className={classes.errorShell}>
            <Text size="lg" fw={600}>{error === 'Snapshot link has expired.' ? 'This snapshot has expired' : 'Snapshot unavailable'}</Text>
            <Text mt={8} size="sm" color="dimmed">
              {error === 'Snapshot link has expired.'
                ? 'This link has expired. Ask the sender to generate a new one.'
                : (error || 'This snapshot could not be loaded.')}
            </Text>
          </div>
        </Card>
      </Container>
    );
  }

  const dashboardType: 'business' | 'marketing' = snapshot.dashboard_type === 'marketing' ? 'marketing' : 'business';
  const dashboardName = snapshot.dashboard_name || (dashboardType === 'business' ? 'Business Dashboard' : 'Marketing Dashboard');
  const frozen = snapshot.frozen_data || {};
  const businessData = frozen.business || {};
  const marketingOverview = frozen.marketing_overview || {};
  const marketingDiagnosis = frozen.marketing_diagnosis || {};
  const settings = snapshot.settings || {};

  // Scope labels were resolved ONCE server-side at snapshot creation time.
  // The viewer has no session to decrypt IDs itself, so it always reads the
  // pre-resolved names here. Falls back to whatever the frozen payload has.
  const scopeLabels = snapshot.scope_labels || {};
  const campaignNames: string[] = Array.isArray(scopeLabels.campaign_names) ? scopeLabels.campaign_names : [];
  const reportName = scopeLabels.report_name || '';
  const primaryGoalName =
    scopeLabels.primary_goal_name ||
    marketingOverview?.overview_cards?.primary_goal_name ||
    businessData?.cards?.primary_goal_name ||
    'Primary Goal';
  const revenueGoalName =
    scopeLabels.revenue_goal_name ||
    businessData?.cards?.revenue_event_name ||
    'Not configured';

  const costThreshold = Number(settings.cost_threshold ?? 20);
  const dateRangeLabel = snapshot.generated_at
    ? `Snapshot from ${new Date(Number(snapshot.generated_at) * 1000).toLocaleDateString()}`
    : 'Snapshot';

  // Build synthetic campaignOptions so ScopeLine can render real names even
  // though the viewer never fetched the campaign list. We use the index as
  // the value since the encrypted IDs are what settings stored.
  const syntheticCampaignOptions = campaignNames.map((name, idx) => ({
    value: settings?.selected_campaign_ids?.[idx] || String(idx),
    label: name,
  }));
  const syntheticCampaignIds = syntheticCampaignOptions.map((o) => o.value);

  // No-op navigation handlers — snapshot viewers cannot navigate into the app.
  const noop = () => {};

  return (
    <Container size="xl" pt={24} pb={30}>
      <Card className={classes.shell}>
        {/* Snapshot top bar — no interactive controls */}
        <Group position="apart" className={classes.topbar} align="center">
          <Group spacing="md">
            <Text className={classes.dashName}>{dashboardName}</Text>
            <span className={classes.snapshotBadge}>READ-ONLY SNAPSHOT</span>
          </Group>
          <Text size="xs" color="dimmed">{dateRangeLabel}</Text>
        </Group>

        {/* Expiry notice */}
        <div className={classes.expiryBar}>
          This is a frozen snapshot. Data will not update. Ask the sender for a new link if you need fresh numbers.
        </div>

        {/* Scope line — uses pre-resolved names from snapshot.scope_labels
            so the viewer shows real campaign / report / goal names instead
            of raw encrypted IDs. */}
        <ScopeLine
          campaigns={syntheticCampaignIds}
          reportName={reportName || 'Not configured'}
          primaryGoal={primaryGoalName}
          revenueGoal={revenueGoalName}
          campaignOptions={syntheticCampaignOptions}
        />

        {dashboardType === 'business' && (
          <BusinessDashboardView
            data={businessData}
            costThreshold={costThreshold}
            dateRangeLabel={dateRangeLabel}
            onNavigateToReports={noop}
            onNavigateToTests={noop}
          />
        )}

        {dashboardType === 'marketing' && (
          <MarketingDashboardView
            overviewData={marketingOverview}
            diagnosisData={marketingDiagnosis}
            primaryGoalName={primaryGoalName}
            costThreshold={costThreshold}
            dateRangeLabel={dateRangeLabel}
            selectedDayCount={30}
            campaignCount={syntheticCampaignIds.length}
            projectionPct={2.0}
            onProjectionChange={noop}
            onNavigateToReports={noop}
            onNavigateToTests={noop}
            // Snapshot data is already loaded by the page-level loader above,
            // so loading=false. The projection slider stays inert because
            // onProjectionChange is a noop — Mantine's controlled Slider will
            // snap the thumb back to projectionPct={2.0} on release.
            loading={false}
          />
        )}
      </Card>
    </Container>
  );
}

export default DashboardSnapshot;
